// Lib
import React from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@milanote/roma/utils/cx';
import { ChevronLeft, ChevronRight, LinkExternal01, Download01, Pencil02, MagicWand02 } from '@untitledui/icons';

// Utils
import {
    getElementId,
    getFileMimeType,
    getImageProp,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { getFilenameFromUrl } from '../../imageModalFileUtils';
import { getLargestImageSize } from '../../imageModalUtils';
import { getImageSource } from '../../../imageHelper';
import { registerDownloadSource } from '../../../../../utils/fileDownloader/javascriptToSwiftFileDownloads';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../../feature/elementFeatureSelector';

// Components
import DownloadButton from '../../../../../components/buttons/DownloadButton';
import DownloadOpenLinkInBrowserButton from '../../../../../components/buttons/DownloadOpenLinkInBrowserButton';

// Constants
import { ExperimentId } from '../../../../../../common/experiments/experimentsConstants';

// Types
import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type ImageModalV2PrimaryToolsProps = {
    element: ImMNElement;
    mode: ImageModalV2Mode;
    isEditable: boolean;
    showDownloadOption: boolean;
    isPresentationMode: boolean;
    canNavigate: boolean;
    navigateToPreviousImage: () => void;
    navigateToNextImage: () => void;
    setMode: (mode: ImageModalV2Mode) => void;
};

const toolButtonClassName = ({ isActive = false, isDisabled = false } = {}) =>
    cx(
        'flex items-center justify-center p-2 transition-[background,color,opacity] duration-150 ease-in-out border-0 rounded-md bg-transparent text-[var(--palette-white-opacity-85)] no-underline',
        isDisabled
            ? 'cursor-default opacity-40'
            : 'cursor-pointer hover:bg-[var(--palette-white-opacity-10)] hover:text-[var(--palette-white)]',
        isActive && 'bg-[var(--palette-white-opacity-20)] text-[var(--palette-white)]',
    );

const ImageModalV2PrimaryTools: React.FC<ImageModalV2PrimaryToolsProps> = ({
    element,
    mode,
    isEditable,
    showDownloadOption,
    isPresentationMode,
    canNavigate,
    navigateToPreviousImage,
    navigateToNextImage,
    setMode,
}) => {
    const isActive = (target: ImageModalV2Mode) => mode === target;

    const toggleMode = (target: ImageModalV2Mode) => setMode(isActive(target) ? ImageModalV2Mode.VIEW : target);

    const elementId = getElementId(element);
    const imageType = getFileMimeType(element) || 'image/jpeg';
    const imageDetails = getImageProp(element);
    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF, true),
    );

    const largestImageUrl = getImageSource({
        imageDetails,
        imageSize: getLargestImageSize(imageDetails),
        elementId,
        useSecureMediaUrl: true,
        optimizeForWebRender: false,
        renderSecureImagesThroughCF,
    });

    const filename = largestImageUrl ? getFilenameFromUrl(largestImageUrl, imageType) : '';

    return (
        <div className="ImageModalV2PrimaryTools pointer-events-auto flex items-center justify-center gap-0.5">
            <button
                type="button"
                className={cx('ImageModalV2PrimaryTools-button nav', toolButtonClassName({ isDisabled: !canNavigate }))}
                disabled={!canNavigate}
                onClick={navigateToPreviousImage}
                title="Previous image"
            >
                <ChevronLeft size={20} />
            </button>
            <button
                type="button"
                className={cx('ImageModalV2PrimaryTools-button nav', toolButtonClassName({ isDisabled: !canNavigate }))}
                disabled={!canNavigate}
                onClick={navigateToNextImage}
                title="Next image"
            >
                <ChevronRight size={20} />
            </button>

            <div className="ImageModalV2PrimaryTools-divider mx-1.5 h-5 w-px bg-[var(--palette-white-opacity-20)]" />

            {largestImageUrl && showDownloadOption && (
                <DownloadOpenLinkInBrowserButton
                    url={largestImageUrl}
                    className={cx('ImageModalV2PrimaryTools-button', toolButtonClassName())}
                >
                    <LinkExternal01 size={18} />
                </DownloadOpenLinkInBrowserButton>
            )}

            {largestImageUrl && showDownloadOption && !isPresentationMode && (
                <DownloadButton
                    url={largestImageUrl}
                    filename={filename}
                    elementId={elementId}
                    onDownloadStart={() => registerDownloadSource('.ImageModalV2PrimaryTools-downloadButton')}
                    isShare={false}
                    showDownloadProgress={false}
                >
                    <span
                        className={cx(
                            'ImageModalV2PrimaryTools-button ImageModalV2PrimaryTools-downloadButton',
                            toolButtonClassName(),
                        )}
                    >
                        <Download01 size={18} />
                    </span>
                </DownloadButton>
            )}

            {isEditable && (
                <button
                    type="button"
                    className={cx(
                        'ImageModalV2PrimaryTools-button',
                        toolButtonClassName({ isActive: isActive(ImageModalV2Mode.DRAW) }),
                    )}
                    onClick={() => toggleMode(ImageModalV2Mode.DRAW)}
                    title="Draw"
                >
                    <Pencil02 size={18} />
                </button>
            )}

            {isEditable && !isPresentationMode && (
                <button
                    type="button"
                    className={cx(
                        'ImageModalV2PrimaryTools-button',
                        toolButtonClassName({ isActive: isActive(ImageModalV2Mode.CROP) }),
                    )}
                    onClick={() => toggleMode(ImageModalV2Mode.CROP)}
                    title="Edit image"
                >
                    <MagicWand02 size={18} />
                </button>
            )}
        </div>
    );
};

export default ImageModalV2PrimaryTools;
